import { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/helper';

export interface DropdownItemType {
  label: string;
  value?: string;
  onClick?: () => void;
}

interface DropdownProps {
  label: string;
  hideLabel?: boolean;
  alignment?: 'left' | 'center' | 'right';
  renderItem?: (item: DropdownItemType, isSelected: boolean) => React.ReactNode;
  defaultValue?: { value: string; label: string } | null;
  items: DropdownItemType[];
}

const CustomDropdown = ({ label, items, alignment = 'right', renderItem, defaultValue }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [selectedLabel, setSelectedLabel] = useState(defaultValue || items[0].label);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const alignmentClasses = {
    left: 'left-0',
    center: 'left-1/2 transform -translate-x-1/2',
    right: 'right-0',
  };

  const handleItemClick = (item: DropdownItemType) => {
    setSelectedLabel(item.label);
    setIsOpen(false);
    item.onClick?.();
  };

  return (
    <div className='relative inline-block text-left' ref={dropdownRef}>
      <button aria-expanded={isOpen} aria-haspopup='menu' onClick={() => setIsOpen((p) => !p)} className='inline-flex items-center justify-between px-[19px] bg-white-100 dark:bg-jet-black-100  rounded-sm dark:border-coal-border border-coal-50 border transition h-[50px] min-w-28 w-max'>
        <p className='flex items-center gap-3'>
          {label && <strong className='text-sm text-coal-250 dark:text-white font-medium'>{label}</strong>}
          <span className='text-sm text-coal-250 dark:text-white font-light'>{typeof selectedLabel === 'string' ? selectedLabel : selectedLabel.label}</span>
        </p>
        <svg className={`ml-2 h-4 w-4 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox='0 0 20 20' fill='currentColor'>
          <path fillRule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.186l3.71-3.956a.75.75 0 111.08 1.04l-4.25 4.531a.75.75 0 01-1.08 0L5.25 8.27a.75.75 0 01-.02-1.06z' clipRule='evenodd' />
        </svg>
      </button>

      <div className={cn('z-20 absolute mt-2 w-full origin-top rounded-sm bg-white-100 dark:bg-jet-black-100 shadow-lg dark:border-coal-border border-coal-50 border  transition-all duration-200 ease-out', isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none', alignmentClasses[alignment])}>
        <ul className='py-1'>
          {items.map((item) => {
            const isSelected = (item.value || item.label) === (typeof selectedLabel === 'string' ? selectedLabel : selectedLabel.value);
            return renderItem ? (
              <div key={item.value || item.label}>{renderItem(item, isSelected)}</div>
            ) : (
              <li key={item.value || item.label}>
                <button onClick={() => handleItemClick(item)} className={cn('w-full px-[19px] py-1 text-center text-coal-250 dark:text-white text-sm transition', isSelected ? 'bg-white-100 dark:bg-jet-black-100' : ' dark:hover:bg-jet-black')}>
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default CustomDropdown;
